import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AccountService, type AccountsFilters } from '../api/account-service';
import type { PaginatedResult, PaginationDto } from '../types/common';
import type { AccountDoc } from '../types/account';

export const accountQueryKeys = {
  all: ['accounts'] as const,
  list: (filters: AccountsFilters = {}, pagination: PaginationDto = {}) =>
    ['accounts', 'list', filters, pagination] as const,
  byId: (id: string | null) => ['accounts', id] as const,
};

export function useAccounts(
  filters?: AccountsFilters,
  pagination?: PaginationDto,
): UseQueryResult<PaginatedResult<AccountDoc>> {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: accountQueryKeys.list(filters, pagination),
    queryFn: async () => {
      const result = await AccountService.getAccounts(filters, pagination);
      for (const account of result.items) {
        queryClient.setQueryData(accountQueryKeys.byId(account._id), account);
      }
      return result;
    },
  });
}

export function useAccount(id: string | null): UseQueryResult<AccountDoc> {
  return useQuery({
    queryKey: accountQueryKeys.byId(id),
    queryFn: () => AccountService.getAccount(id as string),
    enabled: Boolean(id),
  });
}
